import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as JsPlumb from '@jsplumb/browser-ui';
import {
    BrowserJsPlumbInstance,
    Connection,
    EVENT_CONNECTION_CLICK,
    EVENT_CONNECTION_MOUSEOUT,
    EVENT_CONNECTION_MOUSEOVER,
} from '@jsplumb/browser-ui';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';

import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import { ColorEnum } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import {
    FiberLink,
    LinkToDelete,
    LinkTypeEnum,
    SynopticPlanItem,
    TubeLink,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { FiberEditModalComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/fiber-edit-modal/fiber-edit-modal.component';
import { CableDirectionEnum, SynopticBoxPlanCable, SynopticBoxPlanCableBySide } from '@connect-services/client/sdk/api';

type ConnectionOptions = {
    color: ColorEnum;
    isContinuousLink: boolean;
    label?: string;
    link?: LinkToDelete;
    side: CableDirectionEnum;
    source: HTMLElement;
    target: HTMLElement;
};

type FiberKeys = {
    cable: keyof Omit<SynopticPlanItem, 'est_en_continuite'>;
    fiber: keyof Omit<SynopticPlanItem, 'est_en_continuite'>;
    fiberNumber: keyof Omit<SynopticPlanItem, 'est_en_continuite'>;
    tube: keyof Omit<SynopticPlanItem, 'est_en_continuite'>;
};

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanEditDrawerService {
    public readonly deleteLink = new Subject<LinkToDelete>();

    private readonly dialog = inject(MatDialog);
    private jsPlumbInstance: BrowserJsPlumbInstance | null = null;
    private readonly translocoService = inject(TranslocoService);

    public destroyConnections(): void {
        this.jsPlumbInstance?.destroy();
        this.jsPlumbInstance = null;
    }

    public drawCassetteFibers(cassettes: SynopticPlanItem[], cassetteKey: string, containerRef: HTMLElement): void {
        [CableDirectionEnum.LEFT, CableDirectionEnum.RIGHT].forEach((direction: CableDirectionEnum) => {
            this.drawCableCassetteFibers(cassettes, cassetteKey, containerRef, direction);
        });
    }

    public drawTubeCassetteFibers(
        cables: SynopticBoxPlanCableBySide,
        side: CableDirectionEnum,
        cassetteRef: HTMLElement,
        containerRef: HTMLElement,
        cassetteItems: SynopticPlanItem[],
        cassetteKey: string,
    ): void {
        Object.values(cables)
            .flat()
            .forEach((cable: SynopticBoxPlanCable) => {
                const tubesLinked = new Set<number>();
                cassetteItems.forEach((cassetteItem: SynopticPlanItem) => {
                    if (side === CableDirectionEnum.LEFT && cassetteItem.cable_entrant === cable.code) {
                        tubesLinked.add(Number(cassetteItem.numero_tube_entrant));
                    } else if (side === CableDirectionEnum.RIGHT && cassetteItem.cable_sortant === cable.code) {
                        tubesLinked.add(Number(cassetteItem.numero_tube_sortant));
                    }
                });

                const linkToDelete: Omit<TubeLink, 'tube'> = {
                    cable: cable.code,
                    cassetteKey,
                    side,
                    type: LinkTypeEnum.TUBE,
                };

                this.drawTubeCassettes(
                    tubesLinked,
                    `boxPlan_tube_${cable.code}`,
                    cassetteRef,
                    containerRef,
                    side,
                    linkToDelete,
                );
            });
    }

    public reset(): void {
        this.jsPlumbInstance?.batch(() => {
            this.jsPlumbInstance?.deleteEveryConnection();
        });

        this.jsPlumbInstance?.repaintEverything();
    }

    private addConnection(connectionOptions: ConnectionOptions): void {
        if (!this.jsPlumbInstance) {
            return;
        }

        const connection = this.jsPlumbInstance.connect({
            anchors: ['Right', 'Left'],
            connector: { options: { curviness: 100 }, type: 'Bezier' },
            data: { color: connectionOptions.color, linkToDelete: connectionOptions.link },
            endpoint: 'Blank',
            endpointStyle: { fill: connectionOptions.color },
            paintStyle: {
                outlineStroke: '#1f2f3c',
                outlineWidth: 1,
                stroke: connectionOptions.color,
                strokeWidth: 5,
            },
            source:
                connectionOptions.side === CableDirectionEnum.RIGHT
                    ? connectionOptions.target
                    : connectionOptions.source,
            target:
                connectionOptions.side === CableDirectionEnum.RIGHT
                    ? connectionOptions.source
                    : connectionOptions.target,
        });
        if (connectionOptions.label) {
            const translatedState = this.translocoService.translate('state');
            const translatedValue = this.translocoService.translate(
                connectionOptions.isContinuousLink ? 'discontinuous' : 'continuous',
            );
            connection.addOverlay({
                options: {
                    cssClass: 'synoptic-fiber-tooltip-label',
                    id: 'hoverLabel',
                    label: `${connectionOptions.label}\n\n ${translatedState} : ${translatedValue}`,
                    location: 0.5,
                },
                type: 'Label',
            });
            connection.hideOverlay('hoverLabel');
        }
        if (connectionOptions.isContinuousLink) {
            connection.addClass('dashed-connector');
        }
    }

    private addConnectionEvents(): void {
        if (!this.jsPlumbInstance) {
            return;
        }

        this.jsPlumbInstance.bind(
            EVENT_CONNECTION_MOUSEOVER,
            (instance: Connection & { paintStyle: { stroke: ColorEnum } }) => {
                instance.showOverlay('hoverLabel');
            },
        );

        this.jsPlumbInstance.bind(EVENT_CONNECTION_MOUSEOUT, (instance: Connection) => {
            instance.hideOverlay('hoverLabel');
        });

        this.jsPlumbInstance.repaintEverything();
        this.jsPlumbInstance.bind(EVENT_CONNECTION_CLICK, (connection: Connection, event: PointerEvent) => {
            const linkToDelete: LinkToDelete | undefined = connection.getData()?.['linkToDelete'];
            if (!linkToDelete) {
                return;
            }

            this.dialog
                .open<FiberEditModalComponent, void, boolean>(FiberEditModalComponent, {
                    position: { left: `${event.clientX}px`, top: `${event.clientY}px` },
                })
                .afterClosed()
                .subscribe((shouldDelete: boolean | undefined) => {
                    if (!shouldDelete) {
                        return;
                    }

                    this.deleteLink.next(linkToDelete);
                });
        });
    }

    private connectCassetteToFibers(
        cassetteKey: string,
        fiber: SynopticPlanItem,
        sideKeys: FiberKeys,
        side: CableDirectionEnum,
        linkToDelete: FiberLink,
    ): void {
        if (!fiber[sideKeys.fiber]) {
            return;
        }
        const source = document.getElementById(
            `boxPlan_tube_${fiber[sideKeys.cable]}_${fiber[sideKeys.tube]}_${side}_${fiber[sideKeys.fiber]}`,
        );

        const target = document.getElementById(
            `boxPlan_fibre_${cassetteKey}_${fiber[sideKeys.cable]}_${fiber[sideKeys.fiber]}`,
        );

        if (!source || !target) {
            return;
        }

        this.addConnection({
            color: SynopticStandardUtils.formatColor(Number(fiber[sideKeys.fiberNumber])),
            isContinuousLink: !fiber.est_en_continuite,
            label: fiber[sideKeys.fiber]?.toString(),
            link: linkToDelete,
            side: side,
            source: source,
            target: target,
        });
    }

    private drawCableCassetteFibers(
        cassette: SynopticPlanItem[],
        cassetteKey: string,
        container: HTMLElement,
        side: CableDirectionEnum,
    ): void {
        if (!this.jsPlumbInstance) {
            this.jsPlumbInstance = JsPlumb.newInstance({ container: container });
        }
        this.jsPlumbInstance.importDefaults({ connectionsDetachable: false });
        this.jsPlumbInstance.unbind(EVENT_CONNECTION_CLICK);
        const sideKeys = this.getFiberKeys(side);

        cassette.forEach((fiber: SynopticPlanItem) => {
            const linkToDelete: FiberLink = {
                cable: fiber[sideKeys.cable],
                cassetteKey,
                fiber: fiber[sideKeys.fiber],
                side,
                type: LinkTypeEnum.FIBER,
            };

            this.connectCassetteToFibers(cassetteKey, fiber, sideKeys, side, linkToDelete);
        });

        this.addConnectionEvents();
        this.jsPlumbInstance.repaintEverything();
    }

    private drawTubeCassettes(
        tubes: Set<number>,
        tubePrefixId: string,
        cassetteRef: HTMLElement,
        container: HTMLElement,
        side: CableDirectionEnum,
        linkToDelete: Omit<TubeLink, 'tube'>,
    ): void {
        if (!this.jsPlumbInstance) {
            this.jsPlumbInstance = JsPlumb.newInstance({ container: container });
        }

        this.jsPlumbInstance.unbind(EVENT_CONNECTION_CLICK);
        this.jsPlumbInstance.importDefaults({ connectionsDetachable: false });

        tubes.forEach((tubeNumber: number) => {
            const tube = document.getElementById(`${tubePrefixId}_${tubeNumber}_${side}`);
            if (!tube) {
                return;
            }

            const tubeLinkToDelete: LinkToDelete = {
                ...linkToDelete,
                tube: tubeNumber,
            };

            this.addConnection({
                color: SynopticStandardUtils.formatColor(tubeNumber),
                isContinuousLink: false,
                link: tubeLinkToDelete,
                side: side,
                source: tube,
                target: cassetteRef,
            });
        });

        this.addConnectionEvents();
        this.jsPlumbInstance.repaintEverything();
    }

    private getFiberKeys(side: CableDirectionEnum): FiberKeys {
        return {
            cable: side === CableDirectionEnum.LEFT ? 'cable_entrant' : 'cable_sortant',
            fiber: side === CableDirectionEnum.LEFT ? 'fibre_entrante' : 'fibre_sortante',
            fiberNumber: side === CableDirectionEnum.LEFT ? 'numero_fibre_entrante' : 'numero_fibre_sortante',
            tube: side === CableDirectionEnum.LEFT ? 'numero_tube_entrant' : 'numero_tube_sortant',
        };
    }
}
